import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUp, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { wgrajDoPoczekalni } from '@/lib/poczekalnia-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  KOPIA BAZY WGRANA NA ZAPAS — dla wyspy, która JESZCZE NIE ISTNIEJE.
 *
 *  ⚠️⚠️ ZAMÓWIONE 08.09.2026: „czy możemy dodać, by backup z komputera można było wrzucać na zasób
 *  backupowy, bez wyspy? żeby był gotowy jak ona powstanie".
 *
 *  ⚠️⚠️ DLACZEGO PRZY KODZIE REJESTRACYJNYM, A NIE PRZY WYSPIE. Wyspy nie da się utworzyć
 *  z portalu: `SyncGroup` rodzi się przy rejestracji pierwszego agenta, z kodu założycielskiego.
 *  Przed przyjazdem maszyny kod jest JEDYNĄ rzeczą, która wie, jak ta wyspa będzie się nazywać —
 *  więc jest też jedynym sensownym miejscem, do którego można przypiąć paczkę „na zapas".
 *
 *  ⚠️ PACZKA POJAWI SIĘ NA EKRANIE KOPII SAMA. Kod dostaje `groupId` w tej samej chwili, w której
 *  rodzi się wyspa, a ekran kopii pyta o poczekalnię z `groupId` — więc nic nie trzeba przenosić
 *  ani pamiętać. Do magazynu wyspy wprowadza ją potem jedno kliknięcie, już przy maszynie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function WgrajNaZapas({ kodId, juzCzeka }: { kodId: string; juzCzeka: number }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const plikRef = useRef<HTMLInputElement>(null);
  /** `null` = nic nie leci. Procent z liczby wysłanych bajtów, więc mówi prawdę. */
  const [procent, setProcent] = useState<number | null>(null);

  const wgraj = useMutation({
    mutationFn: (plik: File) => wgrajDoPoczekalni({ rodzaj: 'KOD', kodId }, plik, setProcent),
    onSuccess: async (r) => {
      setProcent(null);
      toast.success(t('poczek.wgrana').replace('{plik}', r.nazwa));
      await qc.invalidateQueries({ queryKey: ['poczekalnia'] });
    },
    onError: (e: Error) => {
      setProcent(null);
      toast.error(e.message);
    },
  });

  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      {/* ⚠️ Ukryte pole pliku — natywnego `<input type="file">` nie da się ubrać w przycisk. */}
      <input
        ref={plikRef}
        type="file"
        accept=".zip,.bak"
        className="hidden"
        onChange={(e) => {
          const plik = e.target.files?.[0];
          /** ⚠️ Czyścimy pole OD RAZU — inaczej wybranie tego samego pliku drugi raz nic nie zrobi. */
          e.target.value = '';
          if (plik !== undefined) wgraj.mutate(plik);
        }}
      />
      <Button
        size="sm"
        variant="ghost"
        disabled={wgraj.isPending}
        onClick={() => plikRef.current?.click()}
        title={t('poczek.naZapasOpis')}
      >
        {wgraj.isPending ? <Loader2 className="size-4 animate-spin" /> : <FileUp className="size-4" />}
        {t('poczek.naZapas')}
      </Button>
      {/*
        ⚠️⚠️ ILE JUŻ CZEKA — bo bez tej liczby jedyną odpowiedzią na „czy ja to już wgrałem?" jest
        wgranie drugi raz. Przy kopii bazy to kilkaset megabajtów przez łącze klienta.
      */}
      {juzCzeka > 0 && procent === null && (
        <span className="text-[10px] text-muted-foreground">
          {t('poczek.juzCzeka').replace('{ile}', String(juzCzeka))}
        </span>
      )}
      {procent !== null && (
        <span className="text-[10px] tabular-nums text-muted-foreground">
          {procent >= 100 ? t('kopie.wgrywanieKoniec') : `${procent}%`}
        </span>
      )}
    </span>
  );
}
